import React, { useState, useEffect } from 'react';
import {
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Autocomplete,
    TextField,
    Switch,
    FormControlLabel,
    Alert,
    Box,
    Typography
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import BaseDialog from '../common/BaseDialog';
import { AppointmentType } from '../../services/appointmentTypeService';
import { AppointmentTypeShare } from '../../services/appointmentTypeShareService';
import { appointmentTypeShareService } from '../../services/appointmentTypeShareService';
import { businessService } from '../../services/businessService';
import { BusinessUser } from '../../types/business';
import { useAuth } from '../../hooks/useAuth';

interface AppointmentTypeSharesDialogProps {
    open: boolean;
    onClose: () => void;
    appointmentType: AppointmentType;
}

interface ShareFormData {
    selectedUser: BusinessUser | null;
    canWrite: boolean;
}

const initialFormData: ShareFormData = {
    selectedUser: null,
    canWrite: false
};

const AppointmentTypeSharesDialog: React.FC<AppointmentTypeSharesDialogProps> = ({
    open,
    onClose,
    appointmentType
}) => {
    const auth = useAuth();
    const [shares, setShares] = useState<AppointmentTypeShare[]>([]);
    const [formData, setFormData] = useState<ShareFormData>(initialFormData);
    const [editingShare, setEditingShare] = useState<AppointmentTypeShare | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [businessUsers, setBusinessUsers] = useState<BusinessUser[]>([]);

    useEffect(() => {
        if (open) {
            console.log('Dialog opened, loading data for appointment type:', appointmentType);
            loadShares();
            loadBusinessUsers();
        }
    }, [open, appointmentType.id]);

    const loadBusinessUsers = async () => {
        try {
            const users = await businessService.getBusinessUsers();
            // Filter out the current user and users who already have shares
            const filteredUsers = users.filter(user => 
                user.id !== auth?.user?.id && 
                !shares.some(share => share.userEmail === user.email)
            );
            setBusinessUsers(filteredUsers);
        } catch (error) {
            console.error('Error loading business users:', error);
            setError('Failed to load business users');
        }
    };

    const loadShares = async () => {
        try {
            console.log('Loading shares for appointment type:', appointmentType.id);
            setLoading(true);
            const data = await appointmentTypeShareService.getShares(appointmentType.id);
            console.log('Loaded shares:', data);
            setShares(data);
        } catch (error) {
            console.error('Error loading shares:', error);
            setError('Failed to load shares');
        } finally {
            setLoading(false);
        }
    };

    const handleUserChange = (_event: React.SyntheticEvent, value: BusinessUser | null) => {
        setFormData(prev => ({
            ...prev,
            selectedUser: value
        }));
    };

    const handleSwitchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: checked
        }));
    };

    const handleSubmit = async () => {
        try {
            setLoading(true);
            setError(null);
            
            if (!formData.selectedUser) {
                setError('Please select a user');
                return;
            }

            if (editingShare) {
                await appointmentTypeShareService.updateShare(
                    appointmentType.id,
                    editingShare.id,
                    { canWrite: formData.canWrite }
                );
            } else {
                try {
                    await appointmentTypeShareService.createShare(appointmentType.id, {
                        userEmail: formData.selectedUser.email,
                        canWrite: formData.canWrite
                    });
                } catch (error: any) {
                    const errorMessage = error.response?.data?.message || error.message || 'Failed to create share';
                    setError(errorMessage);
                    return;
                }
            }

            await loadShares();
            await loadBusinessUsers(); // Reload business users to update the available options
            setFormData(initialFormData);
            setEditingShare(null);
            setSuccess('Share updated successfully');
        } catch (error: any) {
            console.error('Error updating share:', error);
            const errorMessage = error.response?.data?.message || error.message || 'Failed to update share';
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (share: AppointmentTypeShare) => {
        setEditingShare(share);
        const user = businessUsers.find(u => u.email === share.userEmail) || null;
        setFormData({
            selectedUser: user,
            canWrite: share.canWrite
        });
    };

    const handleDelete = async (share: AppointmentTypeShare) => {
        if (!window.confirm('Are you sure you want to remove this share?')) {
            return;
        }

        try {
            setLoading(true);
            setError(null);
            await appointmentTypeShareService.deleteShare(appointmentType.id, share.id);
            setSuccess('Share deleted successfully');
            await loadShares();
            await loadBusinessUsers(); // Reload business users to update the available options
        } catch (error: any) {
            const errorMessage = error.response?.data?.message || error.message || 'Failed to delete share';
            setError(errorMessage);
            console.error('Error deleting share:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        setFormData(initialFormData);
        setEditingShare(null);
        setError(null);
        setSuccess(null);
    };

    return (
        <BaseDialog 
            open={open} 
            onClose={onClose} 
            title="Share Appointment Type"
            fullWidth
            sx={{
                '& .MuiDialog-paper': {
                    maxWidth: { xs: 'calc(100vw - 10px)', sm: '900px' }
                }
            }}
            actions={
                <>
                    <Button onClick={onClose}>Close</Button>
                    <Button onClick={handleSubmit} variant="contained" disabled={loading}>
                        {editingShare ? 'Update' : 'Share'}
                    </Button>
                </>
            }
        >
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
                
                <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                        {editingShare ? 'Edit Share' : 'Share with Business User'}
                    </Typography>
                    <Autocomplete
                        value={formData.selectedUser}
                        onChange={handleUserChange}
                        options={businessUsers}
                        getOptionLabel={(option) => `${option.firstName} ${option.lastName} (${option.email})`}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Select User"
                                fullWidth
                                sx={{ mb: 2 }}
                            />
                        )}
                        disabled={!!editingShare}
                    />
                    <FormControlLabel
                        control={
                            <Switch
                                checked={formData.canWrite}
                                onChange={handleSwitchChange}
                                name="canWrite"
                            />
                        }
                        label="Can Write"
                    />
                </Box>

                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>User</TableCell>
                                <TableCell>Email</TableCell>
                                <TableCell>Can Write</TableCell>
                                <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={4} align="center">Loading...</TableCell>
                                </TableRow>
                            ) : shares.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={4} align="center">No shares found</TableCell>
                                </TableRow>
                            ) : (
                                shares.map((share) => (
                                    <TableRow key={share.id}>
                                        <TableCell>{share.userFullName}</TableCell>
                                        <TableCell>{share.userEmail}</TableCell>
                                        <TableCell>{share.canWrite ? 'Yes' : 'No'}</TableCell>
                                        <TableCell>
                                            <IconButton onClick={() => handleEdit(share)}>
                                                <EditIcon />
                                            </IconButton>
                                            <IconButton onClick={() => handleDelete(share)}>
                                                <DeleteIcon />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
        </BaseDialog>
    );
};

export default AppointmentTypeSharesDialog; 